// Profiles — pack lifecycle-aware list (built-in / community / mine).
//
// Mirrors prod's `PackSelector.jsx` action set so users can submit
// drafts, fork community packs, and delete their own work without
// having to dive into the Pack Editor first.

import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MissionControlHero } from "@/components/hero/MissionControlHero";
import { api } from "@/lib/api";
import { packsApi } from "@/lib/api/packs";
import { useConfigStore } from "@/lib/store/configStore";
import { useUiStore } from "@/lib/store/uiStore";
import { useAuth } from "@/hooks/useAuth";
import type { PackStatus, Profile } from "@/lib/types";
import { PackLogo } from "@/components/common/PackLogo";

const STATUS_BADGE: Record<
  PackStatus,
  { label: string; bg: string; fg: string }
> = {
  built_in: { label: "Built-in", bg: "#E2E8F0", fg: "#475569" },
  featured: { label: "Featured", bg: "#FCE7C8", fg: "#9A6212" },
  approved: { label: "Community", bg: "#D1FAE5", fg: "#065F46" },
  draft: { label: "Draft", bg: "#FEF3C7", fg: "#92400E" },
  submitted: { label: "Pending review", bg: "#DBEAFE", fg: "#1E40AF" },
  rejected: { label: "Needs changes", bg: "#FEE2E2", fg: "#991B1B" },
};

export function Profiles(): JSX.Element {
  const navigate = useNavigate();
  const auth = useAuth();
  const qc = useQueryClient();
  const pickMode = useConfigStore((s) => s.pickMode);
  const setConfig = useConfigStore((s) => s.setConfig);
  const setAiGenOpen = useUiStore((s) => s.setAiGenOpen);
  const showToast = useUiStore((s) => s.showToast);

  const { data: profiles = [] } = useQuery({
    queryKey: ["profiles"],
    queryFn: api.listProfiles,
  });

  // Bucketed exactly like prod's `getPacks()` ordering:
  //   * Featured → top of list
  //   * Built-in → curated marks
  //   * Approved community
  //   * My drafts / submitted / rejected (only visible to creator anyway)
  const builtIn = profiles.filter(
    (p) => p.status === "built_in" || p.source === "built_in",
  );
  const community = profiles
    .filter((p) => p.status === "approved" || p.status === "featured")
    .sort((a, b) => {
      // Featured first, then by rating desc, then by use count desc.
      const af = a.status === "featured" ? 1 : 0;
      const bf = b.status === "featured" ? 1 : 0;
      if (af !== bf) return bf - af;
      const ar = a.ratingAvg ?? 0;
      const br = b.ratingAvg ?? 0;
      if (ar !== br) return br - ar;
      return (b.useCount ?? 0) - (a.useCount ?? 0);
    });
  const mine = profiles.filter(
    (p) =>
      p.status === "draft" ||
      p.status === "submitted" ||
      p.status === "rejected",
  );

  const useProfile = (p: Profile): void => {
    if (p.status === "draft" || p.status === "submitted" || p.status === "rejected") {
      // Drafts can still be test-driven, but we want to make the
      // intent explicit. Send through Configure with the draft mode.
      pickMode("custom");
    } else {
      pickMode("full");
    }
    setConfig({ profileId: p.id });
    navigate("/configure");
  };

  const editProfile = (p: Profile): void => {
    navigate(`/profiles/${p.id}/edit`);
  };

  // ── Mutations ──────────────────────────────────────────────────────
  const submitMut = useMutation({
    mutationFn: (p: Profile) => packsApi.submit(p.id),
    onSuccess: (_data, p) => {
      showToast(`"${p.name}" submitted for review`);
      void qc.invalidateQueries({ queryKey: ["profiles"] });
      void qc.invalidateQueries({ queryKey: ["packs"] });
    },
    onError: (e) => {
      // Validator errors come back as `detail` — surface verbatim so
      // the user knows what's missing.
      showToast(`Submit failed: ${(e as Error).message}`);
    },
  });

  const forkMut = useMutation({
    mutationFn: (p: Profile) => packsApi.fork(p.id),
    onSuccess: (res) => {
      showToast(`Forked "${res.name}" — editing your copy`);
      void qc.invalidateQueries({ queryKey: ["profiles"] });
      navigate(`/profiles/${res.id}/edit`);
    },
    onError: (e) => showToast(`Fork failed: ${(e as Error).message}`),
  });

  const deleteMut = useMutation({
    mutationFn: (p: Profile) => packsApi.remove(p.id),
    onSuccess: (_data, p) => {
      showToast(`Deleted "${p.name}"`);
      void qc.invalidateQueries({ queryKey: ["profiles"] });
    },
    onError: (e) => showToast(`Delete failed: ${(e as Error).message}`),
  });

  const promoteMut = useMutation({
    mutationFn: ({ p, target }: { p: Profile; target: "approved" | "featured" }) =>
      packsApi.promote(p.id, target),
    onSuccess: (_data, { p, target }) => {
      showToast(`"${p.name}" promoted to ${target}`);
      void qc.invalidateQueries({ queryKey: ["profiles"] });
    },
    onError: (e) => showToast(`Promote failed: ${(e as Error).message}`),
  });

  // ── Action set per pack status ────────────────────────────────────
  const renderActions = (p: Profile): JSX.Element => {
    const status = p.status ?? "draft";
    const isOwner = !!p.createdBy && !!auth.userEmail && p.createdBy === auth.userEmail;
    const canManageOthers = auth.isAdmin || auth.isCurator;

    if (status === "built_in" || status === "approved" || status === "featured") {
      return (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button
            className="btn ghost sm"
            onClick={() => forkMut.mutate(p)}
            disabled={forkMut.isPending}
          >
            Fork & edit
          </button>
          <button className="btn ghost sm" onClick={() => useProfile(p)}>
            Use →
          </button>
        </div>
      );
    }
    if (status === "submitted") {
      return (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
            With curators
          </span>
          {canManageOthers && (
            <>
              <button
                className="btn ghost sm"
                onClick={() =>
                  promoteMut.mutate({ p, target: "approved" })
                }
              >
                → Community
              </button>
              <button
                className="btn ghost sm"
                onClick={() =>
                  promoteMut.mutate({ p, target: "featured" })
                }
              >
                → Featured
              </button>
            </>
          )}
        </div>
      );
    }
    // draft or rejected
    return (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button className="btn ghost sm" onClick={() => editProfile(p)}>
          Edit
        </button>
        <button
          className="btn ghost sm"
          onClick={() => submitMut.mutate(p)}
          disabled={submitMut.isPending}
        >
          {status === "rejected" ? "Resubmit →" : "Submit →"}
        </button>
        {(isOwner || auth.isAdmin) && (
          <button
            className="btn ghost sm"
            style={{ color: "var(--red, #b91c1c)" }}
            onClick={() => {
              if (
                window.confirm(
                  `Delete "${p.name}"? This cannot be undone.`,
                )
              ) {
                deleteMut.mutate(p);
              }
            }}
            disabled={deleteMut.isPending}
          >
            Delete
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 24px 120px" }}>
      <MissionControlHero
        breadcrumb="Profiles"
        title="Company profiles"
        astro={{ pose: "configure", message: "Pick your DNA" }}
        subtitle={`${builtIn.length} built-in · ${community.length} community · ${mine.length} mine`}
        iconGlyph="◆"
        rightSlot={
          <button
            className="btn hero-primary lg"
            style={{ whiteSpace: "nowrap" }}
            onClick={() => setAiGenOpen(true)}
          >
            ✦ Build with Generator
          </button>
        }
      />

      {builtIn.length > 0 && (
        <PackSection
          title="Built-in profiles · maintained by Generator"
          packs={builtIn}
          renderActions={renderActions}
        />
      )}

      {community.length > 0 && (
        <PackSection
          title="Community profiles · approved & featured"
          packs={community}
          renderActions={renderActions}
        />
      )}

      <section style={{ marginTop: 28 }}>
        <div className="label" style={{ marginBottom: 10 }}>
          My profiles · drafts, submissions & rejections
        </div>
        {mine.length === 0 ? (
          <EmptyState
            title="No custom profiles yet"
            blurb="Describe a company, paste a URL, or upload JSON — Generator drafts a profile for any industry it doesn't cover out of the box."
            cta={
              <button
                className="btn primary"
                onClick={() => setAiGenOpen(true)}
              >
                ✦ Build with Generator
              </button>
            }
          />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 12,
            }}
          >
            {mine.map((p) => (
              <ProfileCard
                key={p.id}
                profile={p}
                renderActions={renderActions}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PackSection({
  title,
  packs,
  renderActions,
}: {
  title: string;
  packs: Profile[];
  renderActions: (p: Profile) => JSX.Element;
}): JSX.Element {
  return (
    <section style={{ marginTop: 24 }}>
      <div className="label" style={{ marginBottom: 10 }}>
        {title}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
        }}
      >
        {packs.map((p) => (
          <ProfileCard
            key={p.id}
            profile={p}
            renderActions={renderActions}
          />
        ))}
      </div>
    </section>
  );
}

function ProfileCard({
  profile,
  renderActions,
}: {
  profile: Profile;
  renderActions: (p: Profile) => JSX.Element;
}): JSX.Element {
  const status = profile.status ?? "draft";
  const badge = STATUS_BADGE[status] ?? STATUS_BADGE.draft;
  return (
    <div
      className="card"
      style={{
        padding: 16,
        borderStyle: status === "draft" ? "dashed" : "solid",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <PackLogo profile={profile} size={32} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--ink)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {profile.name}
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
            {profile.industry}
          </div>
        </div>
        <span
          style={{
            fontSize: 10,
            padding: "2px 8px",
            borderRadius: 999,
            background: badge.bg,
            color: badge.fg,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {badge.label}
        </span>
      </div>
      {profile.description && (
        <p
          style={{
            fontSize: 12,
            color: "var(--ink-2)",
            lineHeight: 1.5,
            margin: "0 0 12px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {profile.description}
        </p>
      )}
      {status === "rejected" && profile.reviewNotes && (
        <div
          style={{
            fontSize: 11,
            color: "#991B1B",
            background: "#FEE2E2",
            border: "1px solid #FECACA",
            borderRadius: 6,
            padding: "6px 8px",
            marginBottom: 10,
            lineHeight: 1.4,
          }}
        >
          <strong>Curator feedback:</strong> {profile.reviewNotes}
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: 10,
          borderTop: "1px solid var(--bd-2)",
          fontSize: 11,
          color: "var(--ink-3)",
          gap: 6,
        }}
      >
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {(profile.useCount ?? 0).toLocaleString()} runs
          {profile.ratingCount && profile.ratingCount > 0
            ? ` · ★ ${profile.ratingAvg?.toFixed(1)} (${profile.ratingCount})`
            : ""}
        </span>
        {renderActions(profile)}
      </div>
    </div>
  );
}

function EmptyState({
  title,
  blurb,
  cta,
}: {
  title: string;
  blurb?: string;
  cta?: JSX.Element;
}): JSX.Element {
  return (
    <div
      style={{
        padding: 48,
        textAlign: "center",
        background: "var(--cb-98)",
        borderRadius: 12,
        border: "1px dashed var(--bd)",
      }}
    >
      <img
        src="/assets/astro-happy.png"
        alt=""
        style={{
          height: 110,
          marginBottom: 8,
          filter: "drop-shadow(0 10px 14px rgba(3,45,96,.18))",
        }}
      />
      <div
        style={{ fontSize: 16, fontWeight: 600, color: "var(--ink)" }}
      >
        {title}
      </div>
      {blurb && (
        <div
          style={{
            fontSize: 13,
            color: "var(--ink-2)",
            maxWidth: 440,
            margin: "6px auto 0",
            lineHeight: 1.5,
          }}
        >
          {blurb}
        </div>
      )}
      {cta && <div style={{ marginTop: 20 }}>{cta}</div>}
    </div>
  );
}
